import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import {
  setElevatedAuth,
  setElevatedAuthError,
  selectElevatedAuthIsValid,
  selectHasPendingVerifications,
  setHasPendingVerifications,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import elevatedAuthService from 'src/services/elevatedAuth.service';
import useElevatedAuthRefresh from 'src/hooks/useElevatedAuthRefresh';
import useElevatedAuthExpiryWatcher from 'src/hooks/useElevatedAuthExpiryWatcher';
import AccountHubSkeleton from './AccountHubSkeleton';
import VerifyCodeModal from './VerifyCodeModal';
import PendingVerificationsModal from 'src/components/Ulcm/PendingVerificationsModal';

interface ElevatedAuthGateProps {
  children: ReactNode;
}

const ElevatedAuthGate: FC<ElevatedAuthGateProps> = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [statusChecked, setStatusChecked] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [showPendingVerifications, setShowPendingVerifications] =
    useState(false);
  const hasPendingVerifications = useSelector(selectHasPendingVerifications);

  const elevatedAuth = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.accountHub.elevatedAuth
  );
  const isValid = useSelector(selectElevatedAuthIsValid);
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );

  // Use email first if the user has a real deliverable email (not a fake
  // prontohousing.io placeholder), otherwise fall back to SMS.
  const hasRealEmail =
    !!user?.email && !user.email.endsWith('@prontohousing.io');
  const otpFirstChannel: 'email' | 'sms' = hasRealEmail ? 'email' : 'sms';

  // The user can receive an OTP if they have a verified email (real, not
  // a placeholder) OR a verified phone. Only when BOTH are missing does the
  // pending-verifications backlog actually block them — at that point the only
  // way for them to receive any code is to verify a contact in the modal.
  const hasUsableContact =
    (hasRealEmail && user?.email_verified === true) ||
    user?.phone_verified === true;
  const blockOnPending = hasPendingVerifications && !hasUsableContact;

  const fetchStatus = useCallback(async (): Promise<void> => {
    try {
      const res = await elevatedAuthService.getStatus();
      dispatch(
        setElevatedAuth({
          verified: res.verified,
          expires_at: res.expires_at ?? undefined,
          totp_enabled: res.totp_enabled,
          method: res.method ?? null,
        })
      );
    } catch (err: unknown) {
      const msg =
        (err as { message?: string })?.message ??
        'Failed to check auth status.';
      dispatch(setElevatedAuthError(msg));
    }
  }, [dispatch]);

  // On mount: reconcile with server
  useEffect(() => {
    const reconcileOnMount = async (): Promise<void> => {
      await fetchStatus();
      setStatusChecked(true);
    };
    void reconcileOnMount();
  }, [fetchStatus]);

  // When the session expires and the modal re-appears, re-fetch totp_enabled
  // so we don't show a TOTP prompt for a user who has since disabled it
  // (or, more commonly, show an OTP prompt for a user who *has* TOTP because
  // a reducer reset wiped the local copy). Hold `reconciling` true until the
  // status refresh lands so VerifyCodeModal only mounts with fresh data —
  // VerifyCodeModal's init effect runs exactly once, and if it mounts with
  // a stale totpEnabled=false it will kick off an OTP dispatch that can't
  // be undone.
  const prevIsValid = useRef(isValid);
  useEffect(() => {
    if (prevIsValid.current && !isValid && statusChecked) {
      setReconciling(true);
      const reconcileOnExpiry = async (): Promise<void> => {
        await fetchStatus();
        setReconciling(false);
      };
      void reconcileOnExpiry();
    }
    prevIsValid.current = isValid;
  }, [isValid, statusChecked, fetchStatus]);

  // Activity-based refresh when within 5 min of expiry
  useElevatedAuthRefresh(isValid, elevatedAuth.expires_at);

  // Auto-clear when window expires
  useElevatedAuthExpiryWatcher(elevatedAuth.expires_at);

  const handleVerified = (expiresAt: string, method: 'otp' | 'totp'): void => {
    dispatch(
      setElevatedAuth({
        verified: true,
        expires_at: expiresAt,
        verified_at: new Date().toISOString(),
        method,
      })
    );
  };

  if (!statusChecked) {
    return <AccountHubSkeleton />;
  }

  // After an expiry, children have been unmounted and we're about to show
  // the re-verify modal. Hold the skeleton until fetchStatus has refreshed
  // totp_enabled from the server — otherwise the modal can mount with a
  // stale Redux value and fire an OTP dispatch that can't be cancelled.
  if (!isValid && reconciling) {
    return <AccountHubSkeleton />;
  }

  // Hold the skeleton until the user object is in Redux — otpFirstChannel
  // depends on user.email and the modal's init effect only runs once on mount,
  // so mounting before user is available would lock in the wrong channel.
  if (!isValid && !user) {
    return <AccountHubSkeleton />;
  }

  return (
    <>
      {isValid ? children : null}
      {!isValid && (showPendingVerifications || blockOnPending) && (
        <PendingVerificationsModal
          onAllVerified={() => {
            dispatch(setHasPendingVerifications(false));
            setShowPendingVerifications(false);
            void fetchStatus();
          }}
          onDismiss={() => {
            dispatch(setHasPendingVerifications(false));
            setShowPendingVerifications(false);
            navigate('/renter');
          }}
        />
      )}
      {!isValid && !showPendingVerifications && !blockOnPending && (
        <VerifyCodeModal
          purpose="elevated_auth"
          totpEnabled={elevatedAuth.totp_enabled}
          preferredChannel={otpFirstChannel}
          onSuccess={handleVerified}
          onUnverifiedContact={() => setShowPendingVerifications(true)}
          onSkip={() => navigate('/renter')}
        />
      )}
    </>
  );
};

export default ElevatedAuthGate;
